import { AnimatedSprite, Texture } from 'pixi.js';
import { TextureFactory, FSM, Store } from '../../core';
import { IEventEmitter, Point, StatefulAnimation, StateMap, Transitionable, Vector } from '../../interface';
import { BaseObject } from '../';
import PlayerTextureInfo_0 from './Player_0.json'; // Basic
import PlayerTextureInfo_1 from './Player_1.json'; // Bee
import PlayerTextureInfo_2 from './Player_2.json'; // Bread
import PlayerTextureInfo_3 from './Player_3.json'; // Ghost
import PlayerTextureInfo_4 from './Player_4.json'; // Cucumber
import PlayerTextureInfo_5 from './Player_5.json'; // Princess
import PlayerTextureInfo_6 from './Player_6.json'; // Tomato
import JumpPlayerTextureInfo from '../jumping/Player.json';
import FINAL_ATTACK_1 from './Final_Attack_1.json'; // Bee
import FINAL_ATTACK_2 from './Final_Attack_2.json'; // Bread
import FINAL_ATTACK_3 from './Final_Attack_3.json'; // Ghost
import FINAL_ATTACK_4 from './Final_Attack_4.json'; // Cucumber
import FINAL_ATTACK_5 from './Final_Attack_5.json'; // Princess
import FINAL_ATTACK_6 from './Final_Attack_6.json'; // Tomato

interface IPlayer extends IEventEmitter {
  hp: number;
  score: number;
  velocity: Vector;
  position: Point;
  currentFrame: number;
}

class Player extends BaseObject implements Transitionable {
  public hp: number;
  public score: number;
  public currentFrame: number = 0;
  public invincibilityDT: number;
  public fsm: FSM<IPlayer>;
  private statefulAnimation: StatefulAnimation;
  private animatedSprite: AnimatedSprite;

  constructor() {
    super();
    this.hp = Store.instance.state.hp;
    this.score = Store.instance.state.score;
    this.invincibilityDT = 0;

    const textureInfos = [
      PlayerTextureInfo_0,
      PlayerTextureInfo_1,
      PlayerTextureInfo_2,
      PlayerTextureInfo_3,
      PlayerTextureInfo_4,
      PlayerTextureInfo_5,
      PlayerTextureInfo_6,
    ];
    const textures = TextureFactory.instance.load(textureInfos[Store.instance.state.costume]);
    const jumpTextures = TextureFactory.instance.load(JumpPlayerTextureInfo);
    this.statefulAnimation = {
      IDLE: {
        textures: textures.slice(50, 65),
        animationSpeed: 0.5,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      JUMP_UP: {
        textures: textures.slice(66, 83),
        animationSpeed: 0.5,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      JUMP_DOWN: {
        textures: textures.slice(84, 95),
        animationSpeed: 0.5,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      SLIDE: {
        textures: textures.slice(96, 104),
        animationSpeed: 0.13,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      DIE: {
        textures: jumpTextures.slice(0, 56),
        animationSpeed: 0.4,
        offset: { x: 0, y: 60 },
        loop: false,
      },
      ATTACK: {
        textures: textures.slice(0, 49),
        animationSpeed: 0.7,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      LAST_ATTACK: {
        textures: textures.slice(0, 49),
        animationSpeed: 0.15,
        offset: { x: 0, y: 0 },
        loop: false,
      },
    };

    if (Store.instance.state.costume === 1) {
      this.statefulAnimation.LAST_ATTACK.textures = TextureFactory.instance.load(FINAL_ATTACK_1);
      this.statefulAnimation.LAST_ATTACK.animationSpeed = 0.27;
    } else if (Store.instance.state.costume === 2) {
      this.statefulAnimation.LAST_ATTACK.textures = TextureFactory.instance.load(FINAL_ATTACK_2);
      this.statefulAnimation.LAST_ATTACK.animationSpeed = 0.3;
    } else if (Store.instance.state.costume === 3) {
      this.statefulAnimation.LAST_ATTACK.textures = TextureFactory.instance.load(FINAL_ATTACK_3);
      this.statefulAnimation.LAST_ATTACK.animationSpeed = 0.35;
    } else if (Store.instance.state.costume === 4) {
      this.statefulAnimation.LAST_ATTACK.textures = TextureFactory.instance.load(FINAL_ATTACK_4);
      this.statefulAnimation.LAST_ATTACK.animationSpeed = 0.27;
    } else if (Store.instance.state.costume === 5) {
      this.statefulAnimation.LAST_ATTACK.textures = TextureFactory.instance.load(FINAL_ATTACK_5);
      this.statefulAnimation.LAST_ATTACK.animationSpeed = 0.27;
    } else if (Store.instance.state.costume === 6) {
      this.statefulAnimation.LAST_ATTACK.textures = TextureFactory.instance.load(FINAL_ATTACK_6);
      this.statefulAnimation.LAST_ATTACK.animationSpeed = 0.33;
    }

    this.animatedSprite = new AnimatedSprite([Texture.EMPTY]);
    this.animatedSprite.scale.set(1);
    this.animatedSprite.updateAnchor = true;
    this.animatedSprite.onComplete = () => {
      if (this.state === 'LAST_ATTACK') {
        this.emit('LAST_ATTACK_DONE');
      }
      this.fsm.trigger('ANIMATION_DONE');
    };
    this.fsm = new FSM<IPlayer>(this, this.states, 'IDLE');
    this.animatedSprite.onFrameChange = (currentFrame: number) => {
      if (
        this.state === 'ATTACK' &&
        this.currentFrame < 17 &&
        currentFrame >= 17 &&
        [2, 4, 6].includes(Store.instance.state.costume)
      ) {
        this.emit('PROJECTILE');
      } else if (
        this.state === 'ATTACK' &&
        this.currentFrame < 27 &&
        currentFrame >= 27 &&
        ![2, 4, 6].includes(Store.instance.state.costume)
      ) {
        this.emit('PROJECTILE');
      }

      if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 0 &&
        this.currentFrame < 30 &&
        currentFrame >= 30
      ) {
        this.emit('LAST_ATTACK_HIT');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 1 &&
        this.currentFrame < 44 &&
        currentFrame >= 44
      ) {
        this.emit('LAST_ATTACK_HIT');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 2 &&
        this.currentFrame < 60 &&
        currentFrame >= 60
      ) {
        this.emit('LAST_ATTACK_HIT');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 3 &&
        this.currentFrame < 55 &&
        currentFrame >= 55
      ) {
        this.emit('LAST_ATTACK_HIT');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 3 &&
        this.currentFrame < 31 &&
        currentFrame >= 31
      ) {
        this.emit('PARTICLE');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 4 &&
        this.currentFrame < 42 &&
        currentFrame >= 42
      ) {
        this.emit('LAST_ATTACK_HIT');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 5 &&
        this.currentFrame < 36 &&
        currentFrame >= 36
      ) {
        this.emit('LAST_ATTACK_HIT');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 6 &&
        this.currentFrame < 40 &&
        currentFrame >= 40
      ) {
        this.emit('LAST_ATTACK_HIT');
      }

      if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 2 &&
        this.currentFrame < 7 &&
        currentFrame >= 7
      ) {
        this.emit('THUNDER');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 2 &&
        this.currentFrame < 14 &&
        currentFrame >= 14
      ) {
        this.emit('THUNDER');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 2 &&
        this.currentFrame < 21 &&
        currentFrame >= 21
      ) {
        this.emit('THUNDER');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 2 &&
        this.currentFrame < 28 &&
        currentFrame >= 28
      ) {
        this.emit('METEOR');
        this.emit('METEOR');
        this.emit('METEOR');
        this.emit('THUNDER');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 2 &&
        this.currentFrame < 35 &&
        currentFrame >= 35
      ) {
        this.emit('METEOR');
        this.emit('METEOR');
        this.emit('METEOR');
        this.emit('THUNDER');
      } else if (
        this.state === 'LAST_ATTACK' &&
        Store.instance.state.costume === 2 &&
        this.currentFrame < 42 &&
        currentFrame >= 42
      ) {
        this.emit('METEOR');
        this.emit('METEOR');
        this.emit('METEOR');
        this.emit('THUNDER');
      }
      this.currentFrame = currentFrame;
    };
    this.stage.addChild(this.animatedSprite);
  }

  public trigger(...events: Array<string>): void {
    this.fsm.trigger(...events);
  }

  public get state(): string {
    return this.fsm.state;
  }

  public jump(): void {
    if (['IDLE', 'ATTACK', 'JUMP_DOWN', 'HIT', 'SLIDE'].includes(this.state)) {
      this.velocity.y = -20;
    }
  }

  public transition(prevState: string, nextState: string): void {
    const info = this.statefulAnimation[nextState];
    const flipX = this.animatedSprite.scale.x < 0 ? -1 : 1;

    this.animatedSprite.textures = info.textures;
    this.animatedSprite.x = info.offset.x * flipX;
    this.animatedSprite.y = info.offset.y;
    this.animatedSprite.scale.set(flipX * Math.abs(this.animatedSprite.scale.x), this.animatedSprite.scale.y);
    this.animatedSprite.animationSpeed = info.animationSpeed;
    this.animatedSprite.loop = info.loop;

    this.animatedSprite.gotoAndPlay(0);
  }

  public update(elapsed: number): void {
    super.update(elapsed);
    this.fsm.update();

    if (this.invincibilityDT > 0) {
      this.animatedSprite.alpha = Math.floor(this.invincibilityDT / 5) % 2 === 0 ? 1 : 0.5;
      this.invincibilityDT -= elapsed;
    } else {
      this.invincibilityDT = 0;
      this.animatedSprite.alpha = 1;
    }
  }

  public hit(damage: number): void {
    if (this.invincibilityDT === 0 && this.state !== 'LAST_ATTACK') {
      this.hp -= damage;

      if (this.hp <= 0) {
        this.hp = 0;
        this.fsm.trigger('DIE');
        this.emit('DIE');
      } else {
        this.fsm.trigger('HIT');
      }

      this.vibrator.vibrate(12);
      this.emit('damaged', damage);
      this.invincibilityDT = 180;
    }
  }

  private get states(): StateMap<IPlayer> {
    return {
      LAST_ATTACK: {
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      IDLE: {
        DIE: [{ events: ['DIE'] }],
        LAST_ATTACK: [{ events: ['LAST_ATTACK'] }],
        SLIDE: [{ events: ['SLIDE'] }],
        ATTACK: [{ events: ['ATTACK'] }],
        JUMP_UP: [[{ refs: ['velocity', 'y'] }, '<', 0]],
      },
      JUMP_UP: {
        DIE: [{ events: ['DIE'] }],
        LAST_ATTACK: [{ events: ['LAST_ATTACK'] }],
        JUMP_DOWN: [[{ refs: ['velocity', 'y'] }, '===', 0], '&&', { events: ['LANDING'] }],
      },
      JUMP_DOWN: {
        DIE: [{ events: ['DIE'] }],
        LAST_ATTACK: [{ events: ['LAST_ATTACK'] }],
        ATTACK: [{ events: ['ATTACK'] }],
        SLIDE: [{ events: ['SLIDE'] }],
        JUMP_UP: [[{ refs: ['velocity', 'y'] }, '<', 0]],
        IDLE: [[{ refs: ['velocity', 'y'] }, '===', 0], '&&', { events: ['ANIMATION_DONE'] }],
      },
      DIE: {},
      ATTACK: {
        DIE: [{ events: ['DIE'] }],
        LAST_ATTACK: [{ events: ['LAST_ATTACK'] }],
        ATTACK: [{ events: ['ATTACK'] }, '&&', [{ refs: ['currentFrame'] }, '>=', 27]],
        SLIDE: [{ events: ['SLIDE'] }],
        JUMP_UP: [[{ refs: ['velocity', 'y'] }, '<', 0]],
        IDLE: [[{ refs: ['velocity', 'y'] }, '===', 0], '&&', { events: ['ANIMATION_DONE'] }],
      },
      SLIDE: {
        DIE: [{ events: ['DIE'] }],
        LAST_ATTACK: [{ events: ['LAST_ATTACK'] }],
        SLIDE: [{ events: ['SLIDE'] }],
        ATTACK: [{ events: ['ATTACK'] }],
        JUMP_UP: [[{ refs: ['velocity', 'y'] }, '<', 0]],
        IDLE: [[{ refs: ['velocity', 'y'] }, '===', 0], '&&', { events: ['ANIMATION_DONE'] }],
      },
    };
  }
}

export default Player;
